import React from 'react';
import uuid from 'react-uuid';
import t from 'react-translate';
import { css } from '@emotion/core';

// redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { getPointsConfiguration } from 'redux/selectors/points-configurations';
import {
  addQuestion,
} from 'redux/actions/quizzes';
import { getFlatCourseAliases } from 'redux/selectors/course';

import { DeepPartial } from 'utility-types';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import AttemptHeart from 'quizzes/components/attempt-heart';
import QuestionSetContext, { SavingIndicator } from 'lecture_pages/services/question-set-context';
import { NQuizQuestion, QuizQuestionType } from 'redux/schemas/models/quiz';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import {
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import QuestionPopover, { PopoverVariant } from 'quizzes/components/question-popover';
import ClickableContainer from 'components/clickable-container';
import {
  gray4,
  gray6,
  white,
  primary,
  success,
  gray5,
} from 'styles/global_defaults/colors';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownCustomItem } from 'shared/components/inputs/nv-dropdown';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { getComponentActivity } from 'redux/selectors/quizzes';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import QuestionNavigator from './question-navigator';

type Props = {
  className?: string;
};

export const ITEM_SIZE = largeSpacing;

export const getItemBaseStyles = (disabled: boolean, active: boolean, borderColor: string = gray6) => css`
  border-radius: 6px;
  width: ${ITEM_SIZE}px;
  height: ${ITEM_SIZE}px;
  ${!active && !disabled && css`
    &:hover {
      border: 1px solid ${borderColor};
    }
  `};
`;

const QuizHeader = (props: Props) => {
  const { className } = props;

  const dispatch = useAppDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);
  const { registerSaving } = React.useContext(SavingRegistryContext);
  const [isAddingQuestion, setIsAddingQuestion] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [showDropdown, setShowDropdown] = React.useState(false);

  const attemptsContainerRef = React.useRef(null);
  const addQuestionButtonRef = React.useRef(null);

  const {
    questions,
    lectureComponent,
    currentQuestion,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    currentQuestionResponse,
    canMakeStructuralChanges,
    requiredCorrectQuestionsCount,
    savingIndicatorTimeoutRef,
    setSavingStatus,
  } = React.useContext(QuestionSetContext);

  const {
    isAnswerMode,
    isEditMode,
    isReviewMode,
    isLongAnswerQuestion,
    isStatement,
    isCurrentActivityType,
    dataQaQuestionSetAttributes,
  } = useQuestionSetHelpers();

  const failedQuestionAttempts = (currentQuestion?.completedQuestionAttempts ?? 0) - (currentQuestionResponse?.isCorrect ? 1 : 0);
  const { offsetWidth, offsetHeight } = addQuestionButtonRef?.current || {};
  const activity = useSelector((state) => getComponentActivity(state, lectureComponent));

  React.useEffect(() => {
    if (attemptsContainerRef.current && !isStatement) {
      attemptsContainerRef.current.blur();
    }
  }, [isStatement]);

  const notEditModeQuestions = React.useMemo<DeepPartial<NQuizQuestion>[]>(
    () => {
      if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
        return Array(activity?.totalQuestions || 0)
          .fill(null)
          .map((_, index) => ({
            index,
            id: uuid(),
          }));
      }
      return [];
    },
    [activity?.totalQuestions, isCurrentActivityType],
  );


  const questionsToRender = isEditMode ? questions : notEditModeQuestions;

  const pointsConfiguration = useSelector((state) => (
    getPointsConfiguration(state, activity?.pointsConfiguration || 0)
  ));
  const isAddQuestionDisabled = !canMakeStructuralChanges || isAddingQuestion;

  const styles = css`
    background-color: ${white};

    .questions-container {
      ${isDragging && css`
        max-width: 680px;
        overflow-x: hidden;
        border: 1px dotted ${primary};
        padding-left: ${halfSpacing}px;
        padding-right: ${halfSpacing}px;
        border-radius: ${quarterSpacing}px;
      `}
    }
    .add-question-dropdown {
      padding: 4px 12px;
      border: 1px solid ${isAddQuestionDisabled ? gray4 : primary};
      border-radius: ${quarterSpacing}px;
      height: 35px;
    }

    .header-container {
      width: 100%;

      .quiz-progress {
        margin-bottom: 8px;
      }

      .correct-indicator {
        display: ${isStatement ? 'none' : 'flex'};
        .icon {
          color: ${success};
        }
      }

      .dropdown-container {
        min-width: 180px !important;
        transform: translate(calc(-100% + ${offsetWidth}px), ${offsetHeight}px) !important;
        top: ${halfSpacing}px !important;
        border-color: ${gray5} !important;
        .bs4-dropdown-item {
          padding: ${halfSpacing}px ${threeQuartersSpacing}px ${halfSpacing}px ${threeQuartersSpacing}px !important;
        }
      }
    }
  `;

  const handleAddQuestion = (questionType: string) => {
    const unregister = registerSaving();

    setShowDropdown(false);
    setIsAddingQuestion(true);
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    wrapThunkAction(dispatch(addQuestion({
      questionSetId: activity?.id,
      currentQuestionId: currentQuestion.id,
      isRequired: isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ),
      questionType,
      lectureComponent,
    }))).then(() => {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch((e) => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      setIsAddingQuestion(false);
      unregister();

      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const correctQuestionsCount = activity?.correctAnswersCount;

  const hasMetRequirement = correctQuestionsCount >= requiredCorrectQuestionsCount;

  const questionTypes: NvDropdownCustomItem[] = [
    {
      type: 'custom',
      customItem: (
        <div className='d-flex mt-2 pl-3'>
          <p className='mb-0 text-small semi-bold text-gray-2'>{t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.PICK_A_QUESTION_TYPE()}</p>
        </div>
      ),
    },
    {
      type: 'custom',
      customItem: (
        <ClickableContainer
          onClick={() => handleAddQuestion(QuizQuestionType.MULTIPLE_CHOICE)}
          className='d-flex mt-2 bs4-dropdown-item'
          data-qa={dataQaQuestionSetAttributes.questionModal.multipleChoiceQuestion.addMultipleChoiceQuestion}
          aria-label={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.MULTIPLE_CHOICE()}
        >
          <NvIcon className='d-flex align-items-center' icon='multiple-choice' size='xs-smallest' />
          <p className='mb-0 ml-3'>
            {isCurrentActivityType(ComponentType.SURVEY)
              ? t.SURVEYS.QUESTION_TYPES.MULTIPLE_CHOICE_SINGLE_ANSWER()
              : t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.MULTIPLE_CHOICE()}
          </p>
        </ClickableContainer>
      ),
    },
    {
      type: 'custom',
      customItem: (
        <ClickableContainer
          onClick={() => handleAddQuestion(
            isCurrentActivityType(ComponentType.QUIZ) || isCurrentActivityType(ComponentType.SURVEY)
              ? QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION
              : QuizQuestionType.SHORT_TEXT_QUESTION,
          )}
          className='d-flex bs4-dropdown-item'
          data-qa={dataQaQuestionSetAttributes.questionModal.shortAnswerQuestion.addShortAnswerQuestion}
          aria-label={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.SHORT_ANSWER()}
        >
          <NvIcon className='d-flex align-items-center' icon='length' size='xs-smallest' />
          <p className='mb-0 ml-3'>{t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.SHORT_ANSWER()}</p>
        </ClickableContainer>
      ),
    },
    {
      type: 'custom',
      customItem: (
        <ClickableContainer
          onClick={() => handleAddQuestion(QuizQuestionType.LONG_ANSWER_QUESTION)}
          className='d-flex bs4-dropdown-item'
          data-qa={dataQaQuestionSetAttributes.questionModal.longAnswerQuestion.addLongAnswerQuestion}
          aria-label={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.LONG_ANSWER()}
        >
          <NvIcon className='d-flex align-items-center' icon='length-long' size='xs-smallest' />
          <p className='mb-0 ml-3'>{t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.LONG_ANSWER()}</p>
        </ClickableContainer>
      ),
    },
    {
      type: 'custom',
      customItem: (
        <ClickableContainer
          onClick={() => handleAddQuestion(QuizQuestionType.NUMBER_QUESTION)}
          className='d-flex bs4-dropdown-item'
          data-qa={dataQaQuestionSetAttributes.questionModal.numberAnswerQuestion.addNumberAnswerQuestion}
          aria-label={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.NUMBER()}
        >
          <NvIcon className='d-flex align-items-center semi-bold' icon='number' size='xs-smallest' />
          <p className='mb-0 ml-3'>{t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.NUMBER()}</p>
        </ClickableContainer>
      ),
    },
    {
      type: 'custom',
      customItem: (
        <ClickableContainer
          onClick={() => handleAddQuestion(QuizQuestionType.STATEMENT)}
          className='d-flex bs4-dropdown-item'
          data-qa={dataQaQuestionSetAttributes.questionModal.statement.addStatement}
          aria-label={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.STATEMENT()}
        >
          <NvIcon className='d-flex align-items-center semi-bold' icon='read' size='xs-smallest' />
          <p className='mb-0 ml-3'>{t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.STATEMENT()}</p>
        </ClickableContainer>
      ),
    },
  ];

  switch (lectureComponent?.type) {
    case ComponentType.QUIZ:
    case ComponentType.SURVEY:
      questionTypes.push(
        {
          type: 'custom',
          customItem: (
            <ClickableContainer
              onClick={() => handleAddQuestion(QuizQuestionType.LIKERT_SCALE_QUESTION)}
              className='d-flex bs4-dropdown-item'
              data-qa={dataQaQuestionSetAttributes.questionModal.likertAnswerQuestion.addLikertQuestion}
              aria-label={t.QUIZZES.ALL_QUESTION_VIEW_QUIZ_BASICS.LIKERT_SCALE()}
            >
              <NvIcon className='d-flex align-items-center' icon='3rd-party-settings' size='xs-smallest' />
              <p className='mb-0 ml-3'>{t.QUIZZES.ALL_QUESTION_VIEW_QUIZ_BASICS.LIKERT_SCALE()}</p>
            </ClickableContainer>
          ),
        },
        {
          type: 'custom',
          customItem: (
            <ClickableContainer
              onClick={() => handleAddQuestion(QuizQuestionType.TABLE_NUMBER_QUESTION)}
              className='d-flex bs4-dropdown-item'
              data-qa={dataQaQuestionSetAttributes.questionModal.tableQuestion.tableNumberQuestion.addTableNumberQuestion}
              aria-label={t.QUIZZES.ALL_QUESTION_VIEW_QUIZ_BASICS.TABLE_NUMBER_QUESTION()}
            >
              <NvIcon className='d-flex align-items-center' icon='xls' size='xs-smallest' />
              <p className='mb-0 ml-3'>{t.QUIZZES.ALL_QUESTION_VIEW_QUIZ_BASICS.TABLE_NUMBER_QUESTION()}</p>
            </ClickableContainer>
          ),
        },
        {
          type: 'custom',
          customItem: (
            <ClickableContainer
              onClick={() => handleAddQuestion(QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION)}
              className='d-flex bs4-dropdown-item'
              data-qa={dataQaQuestionSetAttributes.questionModal.tableQuestion.tableShortTextQuestion.addTableShortTextQuestion}
              aria-label={t.QUIZZES.ALL_QUESTION_VIEW_QUIZ_BASICS.TABLE_SHORT_TEXT_QUESTION()}
            >
              <NvIcon className='d-flex align-items-center' icon='xls' size='xs-smallest' />
              <p className='mb-0 ml-3'>{t.QUIZZES.ALL_QUESTION_VIEW_QUIZ_BASICS.TABLE_SHORT_TEXT_QUESTION()}</p>
            </ClickableContainer>
          ),
        },
      );
      break;
    case ComponentType.PROGRESSIVE_QUIZ:
      questionTypes.push({
        type: 'custom',
        customItem: (
          <ClickableContainer
            onClick={() => handleAddQuestion(QuizQuestionType.MATCHING_QUESTION)}
            className='d-flex bs4-dropdown-item'
            data-qa={dataQaQuestionSetAttributes.questionModal.matchingQuestion.addMatchingQuestion}
            aria-label={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.MATCHING()}
          >
            <NvIcon
              className='d-flex align-items-center semi-bold'
              icon='switch'
              size='xs-smallest'
            />
            <p className='mb-0 ml-3'>
              {t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.MATCHING()}
            </p>
          </ClickableContainer>
        ),
      });
      break;
    default:
      break;
  }

  if (isCurrentActivityType(ComponentType.SURVEY)) {
    questionTypes.splice(2, 0, {
      type: 'custom',
      customItem: (
        <ClickableContainer
          onClick={() => handleAddQuestion(QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER)}
          className='d-flex bs4-dropdown-item'
          data-qa={dataQaQuestionSetAttributes.questionModal.multipleChoiceQuestion.addMultipleChoiceQuestion}
          aria-label={t.SURVEYS.QUESTION_TYPES.MULTIPLE_CHOICE_MULTIPLE_ANSWER()}
        >
          <NvIcon className='d-flex align-items-center' icon='multiple-choice' size='xs-smallest' />
          <p className='mb-0 ml-3'>{t.SURVEYS.QUESTION_TYPES.MULTIPLE_CHOICE_MULTIPLE_ANSWER()}</p>
        </ClickableContainer>
      ),
    });
  }

  const getReleasedLessonAddQuestionDisabledText = () => {
    switch (lectureComponent.type) {
      case ComponentType.QUIZ:
      case ComponentType.PROGRESSIVE_QUIZ:
        return t.QUIZZES.ALREADY_RELEASED.ADD_QUESTION(courseAliases);
      case ComponentType.SURVEY:
        return t.SURVEYS.ALREADY_RELEASED.ADD_QUESTION(courseAliases);
      default:
        return '';
    }
  };

  return (
    <div className={`pt-4 pb-4 d-flex justify-content-center ${className}`} css={styles}>
      <div className='header-container'>
        <div className={`d-flex align-items-center mb-2 ${isReviewMode ? 'justify-content-center' : 'justify-content-between'}`}>
          <div className='text-large-body bold'>
            {isStatement ? t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.STATEMENT() : t.QUIZZES.CURRENT_QUESTION_INDICATOR(currentQuestion.displayIndex, activity?.answerableQuestionsCount)}
          </div>
          {isEditMode && (
            <NvDropdown
              show={showDropdown && canMakeStructuralChanges}
              menuClassName='dropdown-container'
              title={t.INSTITUTIONS.CONTENT_LIBRARY.USER_MANAGEMENT.MANAGE_USERS.BUTTON_TEXT()}
              buttonStyle={NvDropdownButtonStyle.CUSTOM}
              onToggle={() => setShowDropdown(!showDropdown)}
              customTarget={() => (
                <NvTooltip enabled={!canMakeStructuralChanges} text={getReleasedLessonAddQuestionDisabledText()}>
                  <div
                    ref={addQuestionButtonRef}
                    className={`${isAddQuestionDisabled ? 'text-gray-4' : 'text-primary'} d-flex flex-row align-items-center add-question-dropdown semi-bold`}
                    data-qa={dataQaQuestionSetAttributes.questionModal.toggleAddQuestionDropdown}
                  >
                    <NvIcon icon={isAddingQuestion ? 'refresh' : 'create-new-post'} size='smallest' className='mr-2 d-flex' />
                    {t.QUIZZES.ADD_QUESTION()}
                  </div>
                </NvTooltip>
              )}
              items={questionTypes}
              allowOverflow
            />

          )}
          {isAnswerMode && (
            <>
              {!!correctQuestionsCount && (() => {
                const requirementText = hasMetRequirement ? t.QUIZZES.REQUIREMENT_MET(correctQuestionsCount) : t.QUIZZES.KEEP_GOING(correctQuestionsCount, requiredCorrectQuestionsCount);

                const text = pointsConfiguration?.passingScoreRequired ? requirementText : t.QUIZZES.CORRECT_SO_FAR(correctQuestionsCount);

                return (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  <div className='correct-indicator align-items-center' tabIndex={0} aria-label={text}>
                    <div className='text-body-large bold mr-2'>{correctQuestionsCount}</div>
                    <NvTooltip text={text}>
                      <QuestionPopover variants={[
                        PopoverVariant.CORRECT_ANSWER,
                        PopoverVariant.REQUIREMENT_MET]}
                      >
                        <NvIcon icon='check' size='small' />
                      </QuestionPopover>
                    </NvTooltip>
                  </div>
                );
              })()}
            </>
          )}
        </div>
        <QuestionNavigator
          className='mb-2'
          questions={questionsToRender}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
        />
        {!isReviewMode && (activity?.questionMaximumAttempts > 1)
          && !(isStatement || isLongAnswerQuestion)
          && (
          <div className='d-flex justify-content-center'>
            <NvTooltip text={t.QUIZZES.SET_QUESTION_ATTEMPTS_FROM_BASICS()} placement='bottom' enabled={isEditMode}>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <div className='d-flex' tabIndex={0} ref={attemptsContainerRef}>
                <div className='label gray-2 mr-2'>{t.QUIZZES.REMAINING_QUESTION_ATTEMPTS()}</div>
                <div className='sr-only'>{activity?.questionMaximumAttempts - failedQuestionAttempts}</div>
                <div className='d-flex flex-row align-items-center'>
                  {Array.from(Array(activity?.questionMaximumAttempts).keys()).map((_, index) => {
                    const isAttemptUsed = isEditMode ? false : (index + 1) > (activity?.questionMaximumAttempts - failedQuestionAttempts);

                    const isFirst = index === 0;
                    const isLast = index === (activity?.questionMaximumAttempts - 1);

                    return (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className='mr-1'
                        data-qa={dataQaQuestionSetAttributes.questionModal.remainingQuestionAttemptHeart}
                        data-qa-id={`${dataQaQuestionSetAttributes.questionModal.remainingQuestionAttemptHeart}_${index + 1}`}
                      >
                        <QuestionPopover
                          variants={[
                            isFirst ? PopoverVariant.FAILED_QUESTTION : undefined,
                            isLast ? PopoverVariant.FIRST_MISTAKE : undefined,
                          ].filter(Boolean)}
                        >
                          <AttemptHeart used={isAttemptUsed} />
                        </QuestionPopover>
                      </div>
                    );
                  })}
                </div>
              </div>
            </NvTooltip>
          </div>
          )}
      </div>
    </div>
  );
};

export default QuizHeader;
